'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { CheckCircle, XCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_FLAGS = [
{ id: 'fi-001', dogName: 'Rocky', profile: 'Rocky — German Shepherd', reason: 'Potentially inappropriate content', uploadedBy: 'j.morrison@email.com', reportedAt: 'Jun 21, 2026', aiScore: 91, photo: "https://images.unsplash.com/photo-1610445244597-cae62963c861", photoAlt: 'Flagged image from Rocky profile' },
{ id: 'fi-002', dogName: 'Max', profile: 'Max — Rottweiler', reason: 'Low quality / spam upload', uploadedBy: 'maxlover99@webmail.org', reportedAt: 'Jun 21, 2026', aiScore: 64, photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fadd7d69-1767948986967.png", photoAlt: 'Flagged image from Max profile' },
{ id: 'fi-003', dogName: 'Bella', profile: 'Bella — Labrador Retriever', reason: 'Copyright concern', uploadedBy: 'user@photoshare.net', reportedAt: 'Jun 20, 2026', aiScore: 45, photo: "https://img.rocket.new/generatedImages/rocket_gen_img_11460bcf0-1772535214441.png", photoAlt: 'Flagged image from Bella profile' },
{ id: 'fi-004', dogName: 'Zeus', profile: 'Zeus — Doberman', reason: 'Image contains human faces without consent', uploadedBy: 'zeusowner@yahoo.com', reportedAt: 'Jun 20, 2026', aiScore: 78, photo: "https://img.rocket.new/generatedImages/rocket_gen_img_14ce4c846-1773200853487.png", photoAlt: 'Flagged image from Zeus profile' },
{ id: 'fi-005', dogName: 'Daisy', profile: 'Daisy — Beagle', reason: 'Suspected stock photo misuse', uploadedBy: 'daisymom@outlook.com', reportedAt: 'Jun 19, 2026', aiScore: 52, photo: "https://images.unsplash.com/photo-1625953103723-86491fc667a0", photoAlt: 'Flagged image from Daisy profile' },
{ id: 'fi-006', dogName: 'Charlie', profile: 'Charlie — Pomeranian', reason: 'Duplicate image upload', uploadedBy: 'charlie.fan@email.com', reportedAt: 'Jun 18, 2026', aiScore: 28, photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0cef646-1777953873862.png", photoAlt: 'Flagged image from Charlie profile' },
{ id: 'fi-007', dogName: 'Coco', profile: 'Coco — French Bulldog', reason: 'AI-generated image (not a real photo)', uploadedBy: 'cocolover@gmail.com', reportedAt: 'Jun 18, 2026', aiScore: 83, photo: "https://img.rocket.new/generatedImages/rocket_gen_img_100d0e931-1773168450025.png", photoAlt: 'Flagged image from Coco profile' }];


export default function FlaggedImagesTable() {
  const [flags, setFlags] = useState(INITIAL_FLAGS);
  const [confirmAction, setConfirmAction] = useState<{id: string;action: string;} | null>(null);

  const handleAction = (id: string, action: string) => {
    setFlags((f) => f.filter((flag) => flag.id !== id));
    setConfirmAction(null);
    toast.success(action === 'approve' ? 'Image approved and restored.' : 'Image removed from profile.');
  };

  return (
    <>
      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground">Image</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Profile</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Flag Reason</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Uploaded By</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Reported</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">AI Confidence</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {flags.map((flag) =>
              <tr key={flag.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-4">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted border border-border">
                      <AppImage src={flag.photo} alt={flag.photoAlt} width={56} height={56} className="object-cover w-full h-full" />
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-700 text-sm text-foreground">{flag.dogName}</p>
                    <p className="text-xs text-muted-foreground">{flag.profile}</p>
                  </td>
                  <td className="px-4 py-4 max-w-[180px]">
                    <p className="text-sm text-foreground line-clamp-2">{flag.reason}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground">{flag.uploadedBy}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{flag.reportedAt}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-sm font-800 stat-badge ${flag.aiScore >= 70 ? 'text-danger' : flag.aiScore >= 45 ? 'text-warning' : 'text-success'}`}>
                        {flag.aiScore}%
                      </span>
                      <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                        className={`h-full rounded-full ${flag.aiScore >= 70 ? 'bg-danger' : flag.aiScore >= 45 ? 'bg-warning' : 'bg-success'}`}
                        style={{ width: `${flag.aiScore}%` }} />
                      
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                      onClick={() => setConfirmAction({ id: flag.id, action: 'approve' })}
                      className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-success hover:bg-green-100 transition-colors"
                      title="Approve image — mark as safe">
                      
                        <CheckCircle size={13} />
                      </button>
                      <button
                      onClick={() => setConfirmAction({ id: flag.id, action: 'remove' })}
                      className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-danger hover:bg-red-100 transition-colors"
                      title="Remove image from profile — this cannot be undone">
                      
                        <XCircle size={13} />
                      </button>
                      <button
                      className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                      title="Preview full image">
                      
                        <Eye size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {flags.length === 0 &&
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle size={40} className="text-success mb-3" />
            <p className="font-700 text-base text-foreground">No flagged images</p>
            <p className="text-sm text-muted-foreground mt-1">All image reports have been reviewed.</p>
          </div>
        }
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.action === 'approve' ? 'Approve Image' : 'Remove Image'}
        description={confirmAction?.action === 'approve' ? 'This will mark the image as safe and dismiss the flag.' : 'This will permanently remove the image from the profile. The owner will not be notified.'}
        confirmLabel={confirmAction?.action === 'approve' ? 'Approve' : 'Remove Image'}
        destructive={confirmAction?.action !== 'approve'}
        onConfirm={() => confirmAction && handleAction(confirmAction.id, confirmAction.action)}
        onCancel={() => setConfirmAction(null)} />
      
    </>);

}